import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import type { Url } from '@rossum/api-client';
import { ExpandableSearch } from '@rossum/components/expandable-search/ExpandableSearch';
import { DetailDrawer, EmptyStateRadar } from '@rossum/ui';
import { IconSearchOff } from '@rossum/ui/icons/tabler';
import {
  Box,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@rossum/ui/material';
import {
  getStatusLabelId,
  toDisplayedStatus,
} from '../helpers/displayedStatus';
import { getActorName } from '../helpers/getActorName';
import {
  applyHistoryFilters,
  applyHistorySearch,
  DEFAULT_HISTORY_FILTERS,
  hasActiveFilters,
  type HistoryFilters,
  type SearchDisplay,
  toActorOptions,
  toFieldOptions,
} from '../helpers/historyFilters';
import { toActivityBlocks } from '../helpers/toActivityBlocks';
import { useDocumentHistory } from '../hooks/useDocumentHistory';
import { useHiddenFieldIds } from '../hooks/useHiddenFieldIds';
import type { ActivityAdapter } from '../model';
import { ActivityBlock } from './ActivityBlock';
import { DocumentHistoryFilters } from './DocumentHistoryFilters';

type DocumentHistoryDrawerProps = {
  annotationId: number;
  schemaUrl: Url;
  open: boolean;
  onClose: () => void;
  // Registry composed at the module edge (see the feature barrel) — keeps core/
  // free of feature imports.
  adapters: ActivityAdapter[];
};

export const DocumentHistoryDrawer = ({
  annotationId,
  schemaUrl,
  open,
  onClose,
  adapters,
}: DocumentHistoryDrawerProps) => {
  const intl = useIntl();
  const { data, isLoading, isError } = useDocumentHistory(annotationId, open);
  const [filters, setFilters] = useState<HistoryFilters>(
    DEFAULT_HISTORY_FILTERS
  );
  const [search, setSearch] = useState('');
  const { data: hiddenIds } = useHiddenFieldIds(schemaUrl, open);

  const entries = useMemo(() => data?.results ?? [], [data]);
  const allBlocks = useMemo(
    () => toActivityBlocks(entries, adapters),
    [entries, adapters]
  );
  const fields = useMemo(() => toFieldOptions(allBlocks), [allBlocks]);
  const actors = useMemo(() => toActorOptions(allBlocks), [allBlocks]);
  const searchDisplay = useMemo<SearchDisplay>(
    () => ({
      statusLabel: status => {
        const displayed = toDisplayedStatus(status);
        return displayed
          ? intl.formatMessage({ id: getStatusLabelId(displayed) })
          : null;
      },
      actorName: source => getActorName(intl, source),
    }),
    [intl]
  );
  const blocks = useMemo(
    () =>
      applyHistorySearch({
        blocks: applyHistoryFilters({ blocks: allBlocks, filters, hiddenIds }),
        search,
        display: searchDisplay,
      }),
    [allBlocks, filters, hiddenIds, search, searchDisplay]
  );
  const isNarrowed = hasActiveFilters(filters) || search.trim() !== '';

  return (
    <DetailDrawer
      open={open}
      onClose={onClose}
      title={intl.formatMessage({ id: 'features.documentHistory.title' })}
    >
      <Stack sx={{ height: 1 }} data-cy="document-history-drawer">
        {/* Top-aligned, with the search kept at its own height instead of
            stretching, so it stays put when the chips wrap onto more lines. */}
        <Stack
          direction="row"
          sx={{ px: 3, pt: 2, pb: 1, gap: 1, alignItems: 'flex-start' }}
        >
          <Box sx={{ flex: 1 }}>
            <DocumentHistoryFilters
              filters={filters}
              setFilters={setFilters}
              actors={actors}
              fields={fields}
              visibilityAvailable={hiddenIds !== undefined}
            />
          </Box>
          <ExpandableSearch
            value={search}
            onChange={setSearch}
            shouldCollapseOnBlur
            sx={{ height: 'auto' }}
            placeholder={intl.formatMessage({
              id: 'features.documentHistory.search',
            })}
            tooltip={intl.formatMessage({
              id: 'features.documentHistory.search',
            })}
          />
        </Stack>
        <Stack sx={{ p: 3, pt: 1, flex: 1, overflowY: 'auto' }}>
          {isLoading ? (
            <Stack
              sx={{
                height: '50vh',
                justifyContent: 'center',
                alignItems: 'center',
              }}
              data-cy="document-history-loading"
            >
              <CircularProgress />
            </Stack>
          ) : isError && entries.length === 0 ? (
            <Stack
              sx={{
                height: '50vh',
                justifyContent: 'center',
                alignItems: 'center',
              }}
              data-cy="document-history-error"
            >
              <Typography variant="h6" color="text.secondary">
                {intl.formatMessage({ id: 'features.documentHistory.error' })}
              </Typography>
            </Stack>
          ) : blocks.length === 0 && entries.length > 0 && isNarrowed ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ py: 6, textAlign: 'center' }}
              data-cy="document-history-no-matches"
            >
              {intl.formatMessage({ id: 'features.documentHistory.noMatches' })}
            </Typography>
          ) : blocks.length === 0 ? (
            <Stack sx={{ flex: 1 }} data-cy="document-history-empty">
              <EmptyStateRadar
                title={intl.formatMessage({
                  id: 'features.documentHistory.emptyState.title',
                })}
                description={intl.formatMessage({
                  id: 'features.documentHistory.emptyState.description',
                })}
                iconProps={{ Icon: IconSearchOff, iconSize: 36 }}
              />
            </Stack>
          ) : (
            <Stack divider={<Divider />}>
              {blocks.map(block => (
                <ActivityBlock key={block.id} block={block} />
              ))}
            </Stack>
          )}
        </Stack>
      </Stack>
    </DetailDrawer>
  );
};
